import { formatDate } from "../format.js";
import "./studentProgressJourney.css";
import type { FamilyStudentGoal, FamilyStudentGoals } from "../studentGoals.js";

function targetLabel(targetDate: string | null): string | null {
  return targetDate ? formatDate(`${targetDate}T12:00:00Z`) : null;
}

function repertoireLabel(goal: FamilyStudentGoal): string | null {
  if (!goal.repertoireTitle) return null;
  return goal.repertoireComposer
    ? `${goal.repertoireComposer} — ${goal.repertoireTitle}`
    : goal.repertoireTitle;
}

function attributionLabel(goal: FamilyStudentGoal): string {
  const author = goal.createdByName ?? "Tutor attribution unavailable";
  return goal.createdAt ? `${author} · added ${formatDate(goal.createdAt)}` : author;
}

/** Family wording is already redacted by the goals API; this component is presentational only. */
export function FamilyGoals({ goals }: { goals: FamilyStudentGoals }) {
  if (goals.active.length === 0 && goals.recentlyAchieved.length === 0) return null;

  return (
    <section aria-labelledby="family-goals-heading">
      <h2 id="family-goals-heading">Goals & milestones</h2>
      {goals.active.length > 0 && (
        <div className="stack" style={{ gap: 10 }}>
          {goals.active.map((goal) => (
            <article className="panel" key={goal.id}>
              <strong>{goal.title}</strong>
              <div className="small muted progress-meta-small">{attributionLabel(goal)}</div>
              {repertoireLabel(goal) && (
                <div className="small muted progress-meta-small">
                  {repertoireLabel(goal)}
                </div>
              )}
              {goal.description && <p className="notes">{goal.description}</p>}
              {targetLabel(goal.targetDate) && (
                <div className="small muted">Target {targetLabel(goal.targetDate)}</div>
              )}
            </article>
          ))}
        </div>
      )}
      {goals.recentlyAchieved.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <h3>Recently achieved</h3>
          <div className="stack" style={{ gap: 8 }}>
            {goals.recentlyAchieved.map((goal) => (
              <article className="panel" key={goal.id}>
                <div className="spread">
                  <strong>{goal.title}</strong>
                  <span className="small muted">
                    {goal.achievedAt ? `Achieved ${formatDate(goal.achievedAt)}` : "Achieved"}
                  </span>
                </div>
                <div className="small muted progress-meta-small">{attributionLabel(goal)}</div>
                {repertoireLabel(goal) && (
                  <div className="small muted progress-meta-small">
                    {repertoireLabel(goal)}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
